/**
 * Typed API client.
 *
 * Every request rides the Cloudflare Access cookie, so `credentials: 'include'`
 * is not optional. There is no token to store, no login screen to build, and
 * nothing to refresh -- Access handles all of it at the edge (plan section 11).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init.headers },
  });

  if (res.status === 401 || res.status === 403) {
    // Keep pending edits alive while the user signs in in another tab.
    throw new ApiError(res.status, 'Your session expired. Sign in in another tab, then retry here.');
  }

  if (!res.ok) {
    let detail: unknown = null;
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string; detail?: unknown };
      if (body.error) message = body.error;
      detail = body.detail ?? null;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message, detail);
  }

  if (res.status === 204) return undefined as T;
  if (!res.headers.get('content-type')?.includes('application/json')) {
    throw new ApiError(401, 'Your session expired. Sign in in another tab, then retry here.');
  }
  return (await res.json()) as T;
}

export const api = {
  conditional: async <T>(path: string, etag: string | null): Promise<{ data: T | null; etag: string | null }> => {
    const res = await fetch(`/api${path}`, { credentials: 'include', cache: 'no-store',
      headers: etag ? { 'If-None-Match': etag } : {} });
    if (res.status === 304) return { data: null, etag };
    if (!res.ok || !res.headers.get('content-type')?.includes('application/json')) {
      throw new ApiError(res.status, res.status === 401 || res.status === 403 || res.redirected
        ? 'Your session expired. Sign in again to sync your list.' : 'Could not refresh the list.');
    }
    return { data: await res.json() as T, etag: res.headers.get('ETag') };
  },
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ------------------------------------------------------------------- types

export interface CadenceRow {
  recipe_id: string;
  title: string;
  last_served_at: string | null;
  times_served: number;
  days_since_served: number | null;
  avg_rating: number | null;
  rating_count: number;
  effort_score: number | null;
  min_repeat_gap_days: number;
  eligible: number;
}

export interface DishIdea {
  id: string;
  idea_text: string;
  notes: string | null;
  created_at: string;
}

export interface ResurfaceResponse {
  today: string;
  shelf: CadenceRow[];
  ideas: DishIdea[];
}

export interface RecipeSummary {
  id: string;
  title: string;
  source_type: string;
  source_url: string | null;
  servings: number | null;
  total_minutes: number | null;
  effort_score: number | null;
  meal_category: MealCategory;
  ingredient_count: number;
  created_at: string;
}

export type MealCategory = 'chicken' | 'beef' | 'pork' | 'salmon' | 'seafood' | 'vegetarian' | 'other';
export type MealCategoryCounts = Record<MealCategory, number>;

export interface AutoPlanOptions {
  open_dates: string[];
  availability: MealCategoryCounts;
  counts: MealCategoryCounts;
}

export interface AutoPlanResult {
  added: number;
  dinners: { date: string; recipe_id: string; title: string; meal_category: MealCategory }[];
}

export interface RecipeIngredientRow {
  id: string;
  ingredient_id: string | null;
  canonical_name: string | null;
  aisle_name: string | null;
  quantity: number | null;
  unit: string | null;
  prep_note: string | null;
  is_optional: number;
  raw_text: string;
  sort_order: number;
}

export interface RecipeDetail {
  recipe: RecipeSummary & { instructions: string | null; min_repeat_gap_days: number };
  ingredients: RecipeIngredientRow[];
  tags: string[];
}

export interface MealSlot {
  id: string;
  meal_plan_id: string;
  date: string;
  meal_type: string;
  recipe_id: string | null;
  recipe_title: string | null;
  freetext: string | null;
  status: string;
  served_on: string | null;
  rationale: string | null;
  total_minutes: number | null;
  effort_score: number | null;
  sort_order: number;
}

export interface WeekPlan {
  plan_id: string;
  week_start: string;
  days: string[];
  slots: MealSlot[];
  grocery_list_id: string | null;
  grocery_list_start: string | null;
  grocery_list_end: string | null;
}

export interface GroceryItem {
  id: string;
  ingredient_id: string | null;
  display_text: string;
  quantity: number | null;
  unit: string | null;
  aisle_id: string | null;
  aisle_name: string | null;
  origin: string;
  /** A cupboard item a recipe needs but nobody shops for (migration 0006). */
  is_pantry: number;
  /** Why this is here when no recipe put it here (R13). Rendered verbatim. */
  origin_note: string | null;
  origin_recipe_ids: string;
  suppressed_by_purchase_id: string | null;
  suppressed_reason: string | null;
  checked: number;
  price_cents: number | null;
  trip_store_id: string | null;
  sort_order: number;
}

export interface GroceryListResponse {
  list: { id: string; name: string; status: string; version: number; meal_plan_id: string | null } | null;
  items: GroceryItem[];
}

export interface ImportedRecipe {
  title: string;
  sourceUrl: string;
  servings: number | null;
  totalMinutes: number | null;
  activeMinutes: number | null;
  ingredientLines: string[];
  instructions: string;
  imageUrl: string | null;
}

export interface IngredientMatch {
  ingredient_id: string;
  canonical_name: string;
  aisle_id: string | null;
  aisle_name: string | null;
  confidence: 'exact' | 'fuzzy';
  distance: number;
}

export interface Store {
  id: string;
  name: string;
  is_default: number;
}

// ------------------------------------------------------- ideas & suggestions

/**
 * One dish idea plus its repeats. The archive says "Pizza" on four separate
 * lists; that is one idea with a count of four, not four ideas (R12).
 */
export interface IdeaGroup {
  id: string;
  ids: string[];
  idea_text: string;
  count: number;
  notes: string | null;
  created_at: string;
}

export interface IdeaPoolResponse {
  ideas: IdeaGroup[];
  /** Rows before grouping, so the screen can say "324 lines, 187 dishes". */
  total: number;
}

export type SuggestionSource = 'your_list' | 'your_recipes' | 'new';

export interface Suggestion {
  dish: string;
  source: SuggestionSource;
  why: string;
  key_ingredients: string[];
  effort: 'quick' | 'medium' | 'project';
}

export interface SuggestResponse {
  read_on_the_household: string;
  suggestions: Suggestion[];
  corpusSize: { ideas: number; recipes: number; ingredients: number };
  costCents: number;
}


/** An item bought most weeks whatever is for dinner (R13). */
export interface StandingItem {
  item_text: string;
  key: string;
  pages: number;
  of_pages: number;
  ingredient_id: string | null;
  aisle_id: string | null;
  /** Rendered verbatim: "on 35 of your 44 lists", or "added by you". */
  evidence: string;
  /** Present only on a row someone declared; the handle for removing it. */
  declared_id?: string;
}

/** A recipe candidate found on one of the household's own blogs (R14). */
export interface RecipeHit {
  url: string;
  title: string;
  host: string;
}

export interface RecipeSearchResponse {
  dish: string;
  hits: RecipeHit[];
  note: string;
  searchError: string | null;
  searched: string[];
  /** Sources that block automated search, so the gap is stated not hidden. */
  skipped: { domain: string; name: string }[];
  cost_cents: number;
}
